// DuplicateBookingModal — surfaced on registration onBlur when the
// server returns one or more matches. Mirrors the legacy "Duplicate
// Registration Detected" modal in book_in.php (see the inline
// `.duplicate-warning-*` styles); two sections side-by-side, each row
// links to the source record.
//
// Buttons: "Continue Anyway" (close, keep typed reg) and
// "Clear Registration" (close, blank the field). The legacy modal uses
// the same two action labels.

import type { DuplicateBookingMatch } from '../../../types/api';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button/Button';
import styles from './DuplicateBookingModal.module.css';

export interface DuplicateBookingModalProps {
  open: boolean;
  registration: string;
  bookings: DuplicateBookingMatch[];
  pending: DuplicateBookingMatch[];
  /** Close + keep the typed registration as-is. */
  onContinue: () => void;
  /** Close + blank the registration field. */
  onClear: () => void;
  /** Called when a row's "View" link is clicked — caller navigates. */
  onViewMatch: (match: DuplicateBookingMatch) => void;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function DuplicateBookingModal({
  open,
  registration,
  bookings,
  pending,
  onContinue,
  onClear,
  onViewMatch,
}: DuplicateBookingModalProps): JSX.Element {
  const total = bookings.length + pending.length;

  return (
    <Modal
      open={open}
      onClose={onContinue}
      title={`Duplicate Registration Detected — ${registration}`}
      size="lg"
      footer={
        <>
          <Button variant="danger" onClick={onClear}>
            Clear Registration
          </Button>
          <Button variant="primary" onClick={onContinue}>
            Continue Anyway
          </Button>
        </>
      }
    >
      <p className={styles.intro}>
        We found <strong>{total}</strong> existing record
        {total === 1 ? '' : 's'} matching <strong>{registration}</strong>.
        Review them before proceeding.
      </p>

      {bookings.length > 0 ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Found in Bookings ({bookings.length})
          </h3>
          <table className={styles.table} aria-label="Matching bookings">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {bookings.map((m) => (
                <tr key={`b-${m.enc_id}`}>
                  <td>
                    <code>{m.reference}</code>
                  </td>
                  <td>{formatDate(m.date)}</td>
                  <td>{m.customer_name}</td>
                  <td>{m.status.replace(/_/g, ' ')}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => onViewMatch(m)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {pending.length > 0 ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Found in Pending ({pending.length})
          </h3>
          <table className={styles.table} aria-label="Matching pending">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Created</th>
                <th>Customer</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {pending.map((m) => (
                <tr key={`p-${m.enc_id}`}>
                  <td>
                    <code>{m.reference}</code>
                  </td>
                  <td>{formatDate(m.date)}</td>
                  <td>{m.customer_name}</td>
                  <td>{m.status.replace(/_/g, ' ')}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => onViewMatch(m)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </Modal>
  );
}
